import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface CheckoutModalProps {
  titleId: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * CheckoutModal — accessible scrim wrapper for the in-chat CheckoutCard.
 *
 * role=dialog + aria-modal + aria-labelledby; initial focus lands on the plan
 * <h1> (announces context, not a payment button); Tab is trapped within the
 * dialog; body scroll is locked; Esc and a mousedown that STARTS on the scrim
 * close it (a drag ending off-card doesn't); focus returns to the opener on
 * unmount. Portaled to <body> so it sits above the fixed chat shell.
 */
export function CheckoutModal({ titleId, onClose, children }: CheckoutModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    opener.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the dialog itself (aria-labelledby => SR announces the plan title)
    // rather than the <h1>, which would paint a focus ring on the heading text.
    const raf = window.requestAnimationFrame(() => dialogRef.current?.focus());

    function focusables(): HTMLElement[] {
      if (!dialogRef.current) return [];
      return Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
        e.preventDefault(); last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first?.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [titleId, onClose]);

  const scrimDown = useRef(false);

  return createPortal(
    <div
      className="rcpt-scrim"
      onMouseDown={(e) => { scrimDown.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => { if (scrimDown.current && e.target === e.currentTarget) onClose(); scrimDown.current = false; }}
    >
      <div ref={dialogRef} className="rcpt-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <button type="button" className="rcpt-x" onClick={onClose} aria-label="Close">✕</button>
        {children}
      </div>
    </div>,
    document.body,
  );
}
